import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { formatTimeLocale } from '../../utils/i18nHelpers';
import { useNotification } from '../../contexts/NotificationContext';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';
import Skeleton from '../../components/Skeleton';

function BookingsContent() {
  const { t, i18n } = useTranslation();
  const { showAlert, showConfirm } = useNotification();
  const { T, S } = useTheme();
  const locale = i18n.language === 'ar' ? 'ar-EG' : 'en-GB';
  const [bookings, setBookings] = useState([]);
  const [shops, setShops] = useState([]);
  const [allBranches, setAllBranches] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedShopId, setSelectedShopId] = useState('');
  const [selectedBranchId, setSelectedBranchId] = useState('');

  const [showWalkIn, setShowWalkIn] = useState(false);
  const [walkSubmitting, setWalkSubmitting] = useState(false);
  const [winShop, setWinShop] = useState('');
  const [winBranch, setWinBranch] = useState('');
  const [winArtist, setWinArtist] = useState('');
  const [winSeat, setWinSeat] = useState('');
  const [winServiceIds, setWinServiceIds] = useState([]);
  const [winDate, setWinDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [winTime, setWinTime] = useState('10:00');
  const [winCustomerName, setWinCustomerName] = useState('');
  const [winCustomerPhone, setWinCustomerPhone] = useState('');
  const [winArtists, setWinArtists] = useState([]);
  const [winSeats, setWinSeats] = useState([]);
  const [winServices, setWinServices] = useState([]);

  const fetchData = async () => {
    try {
      const [bookData, shopData, branchData] = await Promise.all([
        api.getAdminBookings(),
        api.getAdminShops(),
        api.getBranches(),
      ]);
      setBookings(bookData);
      setShops(shopData);
      setAllBranches(branchData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const shopById = (id) => shops.find((s) => s.id === id);
  const branchById = (id) => allBranches.find((b) => b.id === id);

  const getBookingIds = (b) => {
    let sId = b.shop_id;
    let bId = b.branch_id;
    if (!sId && bId) {
      const branch = branchById(bId);
      if (branch) sId = branch.shop_id;
    }
    return { sId, bId };
  };

  const getSalonName = (b) => {
    const { sId } = getBookingIds(b);
    return shopById(sId)?.name;
  };

  const getBranchName = (b) => (b.branch_id ? branchById(b.branch_id)?.name : null);

  const filteredBookings = bookings.filter((b) => {
    const { sId, bId } = getBookingIds(b);
    if (selectedShopId && sId !== parseInt(selectedShopId, 10)) return false;
    if (selectedBranchId && bId !== parseInt(selectedBranchId, 10)) return false;
    return true;
  });

  const filteredBranches = allBranches.filter((b) => !selectedShopId || b.shop_id === parseInt(selectedShopId, 10));

  const winFilteredBranches = useMemo(
    () => allBranches.filter((b) => !winShop || b.shop_id === parseInt(winShop, 10)),
    [allBranches, winShop],
  );

  const branchServices = useMemo(() => {
    if (!winBranch) return [];
    const bid = parseInt(winBranch, 10);
    return winServices.filter((s) => s.branch_id === bid);
  }, [winServices, winBranch]);

  useEffect(() => {
    if (!showWalkIn || !winShop) {
      setWinSeats([]);
      return;
    }
    api.getAdminSeats(parseInt(winShop, 10))
      .then((data) => setWinSeats(Array.isArray(data) ? data : []))
      .catch(() => setWinSeats([]));
  }, [showWalkIn, winShop]);

  useEffect(() => {
    if (!showWalkIn || !winShop) {
      setWinServices([]);
      return;
    }
    api.getAdminServices(parseInt(winShop, 10))
      .then((data) => setWinServices(Array.isArray(data) ? data : []))
      .catch(() => setWinServices([]));
  }, [showWalkIn, winShop]);

  useEffect(() => {
    if (!showWalkIn || !winBranch) {
      setWinArtists([]);
      setWinArtist('');
      return;
    }
    api.getAdminArtists({ branchId: parseInt(winBranch, 10) })
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setWinArtists(list);
        setWinArtist((prev) => {
          if (!list.length) return '';
          const ok = list.some((a) => String(a.id) === String(prev));
          return ok ? prev : String(list[0].id);
        });
      })
      .catch(() => {
        setWinArtists([]);
        setWinArtist('');
      });
  }, [showWalkIn, winBranch]);

  const seatsForBranch = useMemo(() => {
    if (!winBranch) return [];
    const bid = parseInt(winBranch, 10);
    return winSeats.filter((s) => s.branch_id === bid || s.shop_id === parseInt(winShop, 10));
  }, [winSeats, winBranch, winShop]);

  const toggleWinService = (id) => {
    setWinServiceIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const openWalkIn = () => {
    setShowWalkIn(true);
    setWinShop('');
    setWinBranch('');
    setWinArtist('');
    setWinSeat('');
    setWinServiceIds([]);
    setWinDate(new Date().toISOString().slice(0, 10));
    setWinTime('10:00');
    setWinCustomerName('');
    setWinCustomerPhone('');
  };

  const submitWalkIn = async () => {
    if (!winBranch || !winArtist || !winSeat || winServiceIds.length === 0) {
      showAlert(t('admin.walkInMissingFields', 'Please select branch, artist, seat, and at least one service.'));
      return;
    }
    setWalkSubmitting(true);
    try {
      await api.createAdminWalkInBooking({
        branch_id: parseInt(winBranch, 10),
        artist_id: parseInt(winArtist, 10),
        seat_id: parseInt(winSeat, 10),
        service_ids: winServiceIds,
        booking_date: winDate,
        booking_time: winTime,
        customer_name: winCustomerName || undefined,
        customer_phone: winCustomerPhone || undefined,
      });
      showAlert(t('manager.reservationAddedSuccess', 'Booking created'));
      setShowWalkIn(false);
      fetchData();
    } catch (err) {
      showAlert(err.message || t('manager.failedToAddReservation'));
    } finally {
      setWalkSubmitting(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.updateAdminBooking(id, { status });
      fetchData();
    } catch (err) {
      showAlert(err.message);
    }
  };

  const deleteBooking = async (id) => {
    showConfirm(t('admin.deleteBookingConfirm'), async () => {
      try {
        await api.deleteAdminBooking(id);
        fetchData();
      } catch (err) {
        showAlert(err.message);
      }
    });
  };

  const handleShopFilter = (sid) => {
    setSelectedShopId(sid);
    setSelectedBranchId('');
  };

  return (
    <AdminLayout role="admin">
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={S.pageTitle}>{t('admin.allBookings')}</h1>
            <div style={{ width: '40px', height: '2px', background: T.orange, marginBottom: '1.5rem' }} />
          </div>
          <button
            type="button"
            style={S.btnOrange}
            onClick={openWalkIn}
          >
            {t('admin.createWalkIn', '+ Create Walk-in')}
          </button>
        </div>

        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <select
            value={selectedShopId}
            onChange={(e) => handleShopFilter(e.target.value)}
            style={{ ...S.select, width: '200px', fontSize: '0.82rem' }}
          >
            <option value="">{t('admin.allSalons')}</option>
            {shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>

          <select
            value={selectedBranchId}
            onChange={(e) => setSelectedBranchId(e.target.value)}
            style={{ ...S.select, width: '200px', fontSize: '0.82rem' }}
          >
            <option value="">{t('admin.allBranches')}</option>
            {filteredBranches.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>

          {(selectedShopId || selectedBranchId) && (
            <button
              type="button"
              onClick={() => { setSelectedShopId(''); setSelectedBranchId(''); }}
              style={{ ...S.btnOutline, padding: '0.4rem 0.8rem', fontSize: '0.75rem', height: '36px' }}
            >
              {t('admin.clearFilters')}
            </button>
          )}
        </div>
      </div>

      {showWalkIn && (
        <div style={S.modal}>
          <div style={{ ...S.modalBox, maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={S.modalTitle}>{t('admin.walkInTitle', 'Create walk-in booking')}</h2>
            <p style={{ fontSize: '0.82rem', color: T.muted, marginBottom: '1rem' }}>{t('admin.selectShopAndBranch', 'Choose salon, branch, worker, seat, services, date and time.')}</p>

            <label style={{ display: 'block', fontSize: '0.75rem', color: T.muted, marginBottom: '0.35rem' }}>{t('admin.selectSalon')}</label>
            <select value={winShop} onChange={(e) => { setWinShop(e.target.value); setWinBranch(''); setWinArtist(''); setWinSeat(''); setWinServiceIds([]); }} style={S.select}>
              <option value="">{t('admin.chooseSalon')}</option>
              {shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>

            <label style={{ display: 'block', fontSize: '0.75rem', color: T.muted, marginTop: '0.75rem', marginBottom: '0.35rem' }}>{t('admin.selectBranch')}</label>
            <select value={winBranch} onChange={(e) => { setWinBranch(e.target.value); setWinArtist(''); setWinSeat(''); setWinServiceIds([]); }} style={S.select} disabled={!winShop}>
              <option value="">{t('admin.chooseBranch')}</option>
              {winFilteredBranches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>

            <label style={{ display: 'block', fontSize: '0.75rem', color: T.muted, marginTop: '0.75rem', marginBottom: '0.35rem' }}>{t('admin.selectArtist', 'Worker')}</label>
            <select value={winArtist} onChange={(e) => setWinArtist(e.target.value)} style={S.select} disabled={!winBranch}>
              <option value="">{t('admin.selectArtistPlaceholder')}</option>
              {winArtists.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>

            <label style={{ display: 'block', fontSize: '0.75rem', color: T.muted, marginTop: '0.75rem', marginBottom: '0.35rem' }}>{t('admin.selectSeat', 'Seat')}</label>
            <select value={winSeat} onChange={(e) => setWinSeat(e.target.value)} style={S.select} disabled={!winBranch}>
              <option value="">{t('admin.selectSeatPlaceholder')}</option>
              {seatsForBranch.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>

            <label style={{ display: 'block', fontSize: '0.75rem', color: T.muted, marginTop: '0.75rem', marginBottom: '0.35rem' }}>{t('admin.selectServices', 'Services')}</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: 200, overflowY: 'auto', marginBottom: '0.75rem' }}>
              {branchServices.length === 0 ? (
                <span style={{ color: T.muted, fontSize: '0.85rem' }}>{t('manager.noServicesAvailable')}</span>
              ) : (
                branchServices.map((svc) => (
                  <label key={svc.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: T.white, fontSize: '0.88rem' }}>
                    <input
                      type="checkbox"
                      checked={winServiceIds.includes(svc.id)}
                      onChange={() => toggleWinService(svc.id)}
                      style={{ accentColor: T.orange }}
                    />
                    {svc.name}
                    {' '}
                    (
                    {svc.duration_minutes}
                    {' '}
                    min,
                    {' '}
                    {svc.price}
                    {' '}
                    EGP)
                  </label>
                ))
              )}
            </div>

            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 140 }}>
                <label style={{ display: 'block', fontSize: '0.75rem', color: T.muted, marginBottom: '0.35rem' }}>{t('manager.selectDate')}</label>
                <input type="date" value={winDate} onChange={(e) => setWinDate(e.target.value)} style={S.input} />
              </div>
              <div style={{ flex: 1, minWidth: 120 }}>
                <label style={{ display: 'block', fontSize: '0.75rem', color: T.muted, marginBottom: '0.35rem' }}>{t('manager.selectTime', 'Time')}</label>
                <input type="time" value={winTime} onChange={(e) => setWinTime(e.target.value)} style={S.input} />
              </div>
            </div>

            <input placeholder={t('manager.placeholderCustomerNameWalkIn')} value={winCustomerName} onChange={(e) => setWinCustomerName(e.target.value)} style={{ ...S.input, marginTop: '0.75rem' }} />
            <input placeholder={t('manager.placeholderPhoneOptional')} value={winCustomerPhone} onChange={(e) => setWinCustomerPhone(e.target.value)} style={S.input} />

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem' }}>
              <button type="button" onClick={submitWalkIn} disabled={walkSubmitting} style={{ ...S.btnOrange, flex: 1 }}>
                {walkSubmitting ? t('common.saving') : t('manager.confirmWalkInBooking')}
              </button>
              <button type="button" onClick={() => setShowWalkIn(false)} style={S.btnOutline}>{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {loading ? <Skeleton count={5} height="48px" />
        : filteredBookings.length === 0 ? (
          <p style={{ color: T.muted, fontStyle: 'italic' }}>
            {bookings.length === 0 ? t('admin.noBookingsFound') : t('admin.noBookingsMatchFilters')}
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            {filteredBookings.map((b) => {
              const salon = getSalonName(b);
              const branch = getBranchName(b);
              return (
                <div key={b.id} style={{ ...S.card, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                  <div>
                    <h3 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.white, marginBottom: '0.3rem' }}>
                      {b.service_name}
                    </h3>
                    {(salon || branch) && (
                      <p style={{ fontSize: '0.8rem', marginBottom: '0.3rem' }}>
                        {salon && <span style={{ color: T.orange, fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.05em' }}>{salon}</span>}
                        {salon && branch && <span style={{ color: T.muted }}> › </span>}
                        {branch && <span style={{ color: T.muted }}>{branch}</span>}
                      </p>
                    )}
                    <p style={{ fontSize: '0.82rem', color: T.muted }}>
                      📅
                      {b.booking_date}
                      {' '}
                      at
                      {' '}
                      {formatTimeLocale(b.booking_time, locale)}
                    </p>
                    <p style={{ fontSize: '0.82rem', color: T.muted }}>
                      💰
                      {b.service_price}
                      {' '}
                      EGP &nbsp;·&nbsp; User #
                      {b.user_id}
                    </p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <span style={S.badge(b.status)}>{t(`booking.status.${b.status}`, b.status)}</span>
                    {b.status !== 'confirmed' && b.status !== 'no_show' && b.status !== 'cancelled' && (
                      <button type="button" style={S.btnSmall} onClick={() => setStatus(b.id, 'confirmed')}>{t('admin.confirm')}</button>
                    )}
                    {b.status === 'confirmed' && (
                      <button type="button" style={{ ...S.btnSmall, color: '#fdba74' }} onClick={() => setStatus(b.id, 'no_show')}>{t('booking.noShow', 'No Show')}</button>
                    )}
                    {b.status !== 'cancelled' && b.status !== 'no_show' && (
                      <button type="button" style={S.btnSmall} onClick={() => setStatus(b.id, 'cancelled')}>{t('common.cancel')}</button>
                    )}
                    <button type="button" style={S.btnDanger} onClick={() => deleteBooking(b.id)}>{t('common.delete')}</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
    </AdminLayout>
  );
}

export default function Bookings() {
  return <ProtectedRoute requireSuperAdmin><BookingsContent /></ProtectedRoute>;
}
